import React, { useCallback, useEffect, useState } from "react";

/**
 * Drag plumbing shared by the resize dividers (the process detail-panel width and the compare
 * view's split). `begin` runs on pointer-down and returns the move handler for that drag —
 * closing over whatever start geometry it needs — or null to ignore the press. The hook owns
 * pointer capture, listener cleanup and the global drag cursor.
 */
export const useDividerDrag = (
  begin: (event: React.PointerEvent<HTMLDivElement>) => ((moveEvent: PointerEvent) => void) | null,
) => {
  const [resizing, setResizing] = useState(false);

  const startResize = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      const onMove = begin(event);
      if (!onMove) return;
      const handle = event.currentTarget;
      handle.setPointerCapture?.(event.pointerId);
      setResizing(true);
      const onUp = (upEvent: PointerEvent) => {
        setResizing(false);
        handle.releasePointerCapture?.(upEvent.pointerId);
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onUp);
        handle.removeEventListener("pointercancel", onUp);
      };
      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onUp);
      handle.addEventListener("pointercancel", onUp);
    },
    [begin],
  );

  // While dragging, force the resize cursor everywhere and suppress text selection so the drag
  // stays smooth even as the pointer travels over the surrounding content.
  useEffect(() => {
    if (!resizing || typeof document === "undefined") return;
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [resizing]);

  return { resizing, startResize };
};

/**
 * Arrow-key resizing for a divider: ←/→ nudge, Shift for a coarse step, Home to reset. `widenKey`
 * says which arrow grows the thing being resized, since that depends on which side of it the
 * divider sits. Returns the handler to hand to ResizeDivider's `onKeyDown`.
 */
export const useResizeKeys = ({
  step,
  coarseStep,
  widenKey,
  onResize,
  onReset,
}: {
  step: number;
  coarseStep: number;
  widenKey: "ArrowLeft" | "ArrowRight";
  /** Called with the signed delta to apply. */
  onResize: (delta: number) => void;
  onReset: () => void;
}) =>
  useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        const magnitude = event.shiftKey ? coarseStep : step;
        onResize(event.key === widenKey ? magnitude : -magnitude);
      } else if (event.key === "Home") {
        event.preventDefault();
        onReset();
      }
    },
    [step, coarseStep, widenKey, onResize, onReset],
  );

/**
 * Width of a panel pinned to the right edge, resized from a divider on its left: dragging left
 * or pressing ← widens it, and Home or a double-click resets it. With `storageKey` the width is
 * remembered across visits, saved when a drag ends rather than on every move.
 */
export const useRightPanelWidth = ({
  initial,
  min,
  max,
  storageKey,
}: {
  initial: number;
  min: number;
  max: number;
  storageKey?: string;
}) => {
  const clamp = useCallback((width: number) => Math.min(max, Math.max(min, width)), [min, max]);
  const [width, setWidth] = useState(() => {
    try {
      const saved = storageKey ? Number(window.localStorage.getItem(storageKey)) : Number.NaN;
      if (Number.isFinite(saved) && saved > 0) return clamp(saved);
    } catch {
      // Keep resizing available when browser storage is unavailable.
    }
    return initial;
  });
  const { resizing, startResize } = useDividerDrag(event => {
    const startX = event.clientX;
    const startWidth = width;
    return moveEvent => setWidth(clamp(startWidth - (moveEvent.clientX - startX)));
  });
  useEffect(() => {
    if (!storageKey || resizing) return;
    try {
      window.localStorage.setItem(storageKey, String(width));
    } catch {
      // The current width still works when browser storage is unavailable.
    }
  }, [storageKey, resizing, width]);
  const reset = useCallback(() => setWidth(initial), [initial]);
  const onResizeKeyDown = useResizeKeys({
    step: 16,
    coarseStep: 48,
    widenKey: "ArrowLeft",
    onResize: useCallback(delta => setWidth(value => clamp(value + delta)), [clamp]),
    onReset: reset,
  });
  return { width, resizing, startResize, onResizeKeyDown, reset };
};

/**
 * The 1px rule two surfaces meet on, with the drag handle overlaid on it. Wrapping — rather than
 * giving the handle a column of its own — is what keeps the page background from showing through,
 * and is what `overlay` positions against, so the two always travel together.
 */
export const ResizeHairline = ({ children }: { children: React.ReactNode }) => (
  <div className="relative z-10 w-px shrink-0 self-stretch bg-outline">{children}</div>
);

type ResizeDividerProps = {
  label: string;
  /** Current / min / max of the resized dimension, for the separator ARIA values. */
  value: number;
  min: number;
  max: number;
  resizing: boolean;
  onPointerDown: React.PointerEventHandler<HTMLDivElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  onDoubleClick: React.MouseEventHandler<HTMLDivElement>;
  /** Visibility gate for the layout's breakpoint, e.g. "hidden lg:flex". */
  className: string;
  title?: string;
  /** Centre the handle over the caller's own separator line instead of taking a column of its
   *  own. See ResizeHairline. */
  overlay?: boolean;
  /** Extra content inside the handle, e.g. a hover tooltip. */
  children?: React.ReactNode;
};

/**
 * A vertical resize handle: a wide transparent drag target whose visual bar only appears on
 * hover/focus/drag. Keyboard behavior and the drag→size mapping stay with the caller.
 */
export const ResizeDivider = ({
  label,
  value,
  min,
  max,
  resizing,
  onPointerDown,
  onKeyDown,
  onDoubleClick,
  className,
  title,
  overlay = false,
  children,
}: ResizeDividerProps) => (
  <div
    role="separator"
    aria-orientation="vertical"
    aria-label={label}
    title={title}
    aria-valuenow={Math.round(value)}
    aria-valuemin={min}
    aria-valuemax={max}
    tabIndex={0}
    onPointerDown={onPointerDown}
    onKeyDown={onKeyDown}
    onDoubleClick={onDoubleClick}
    className={`group w-3 cursor-col-resize touch-none select-none items-center justify-center rounded focus:outline-none ${
      overlay ? "absolute inset-y-0 left-1/2 -translate-x-1/2" : "relative shrink-0 self-stretch"
    } ${className}`}
  >
    <span
      aria-hidden="true"
      className={`h-12 w-1 rounded-full transition duration-150 ${
        resizing
          ? "bg-text-dim/45 opacity-100"
          : "bg-text-dim/30 opacity-0 group-hover:opacity-100 group-focus:opacity-100"
      }`}
    />
    {children}
  </div>
);
